import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Award, GraduationCap, Stethoscope, Calendar, Video } from 'lucide-react';

const INTERIOR_IMAGE = "https://customer-assets.emergentagent.com/job_handcare-redesign/artifacts/tklejtn1_banner-pic4.jpg";

const features = [
  {
    icon: Award,
    title: 'Board Certified',
    description: 'Orthopedic surgery certification with specialized hand fellowship training'
  },
  {
    icon: GraduationCap,
    title: 'Academic Excellence',
    description: 'Bringing academic-level expertise to community healthcare'
  },
  {
    icon: Stethoscope,
    title: 'Exclusive Focus',
    description: 'Practice limited entirely to hand, wrist, and elbow conditions'
  },
  {
    icon: Video,
    title: 'Telemedicine',
    description: 'HIPAA-compliant virtual consultations available'
  },
];

const credentials = [
  'Fellowship trained in Hand & Upper Extremity Surgery',
  'Board Certified Orthopedic Surgeon',
  'Minimally invasive surgical techniques',
  'Same-day and urgent appointments available',
  'Comprehensive diagnostic imaging on-site',
  'Insurance accepted from most major providers',
];

export const AboutSection = () => {
  return (
    <section id="about" className="py-20 lg:py-28 bg-card overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Image Side */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img 
                src={INTERIOR_IMAGE} 
                alt="Melbourne Hand Center Interior" 
                className="w-full h-auto object-cover"
              />
            </div>
            
            {/* Floating Stats Card */}
            <div className="absolute -bottom-6 -right-6 bg-primary text-primary-foreground rounded-2xl p-6 shadow-primary max-w-xs">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <p className="text-3xl font-bold">1000+</p>
                  <p className="text-sm opacity-90">Patients Treated</p>
                </div>
                <div>
                  <p className="text-3xl font-bold">25+</p>
                  <p className="text-sm opacity-90">Years Combined</p>
                </div>
              </div>
            </div>
          </div>

          {/* Content Side */}
          <div className="space-y-8">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
                <Award className="w-4 h-4" />
                <span className="text-sm font-semibold">Why Choose Us</span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
                Specialized Expertise You Can Trust
              </h2>
              <p className="text-lg text-muted-foreground">
                Dr. Moyles and our team bring academic-quality care to Brevard County. 
                As fellowship-trained specialists, we focus exclusively on conditions 
                affecting the hand, wrist, and elbow – ensuring you receive the most 
                advanced and specialized treatment available.
              </p>
            </div>

            {/* Credentials List */}
            <div className="space-y-3">
              {credentials.map((credential, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  <span className="text-foreground">{credential}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-4">
              <Button variant="hero" size="lg" asChild>
                <a 
                  href="https://app.acuityscheduling.com/schedule.php?owner=13861726"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Schedule Consultation
                </a>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <a href="#team">Meet Our Doctors</a>
              </Button>
            </div>
          </div>
        </div>

        {/* Feature Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-20">
          {features.map((feature, index) => (
            <Card 
              key={feature.title}
              className="bg-gradient-card border-0 shadow-card hover:shadow-lg transition-all duration-300"
            >
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 mx-auto rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {feature.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
